"use client"

import React, { useEffect, useState, useRef } from "react"
import { trackLead } from "@/lib/meta-pixel"

interface HubspotFormProps {
  servicio?: string
  sucursal?: string
}

export default function HubspotForm({
  servicio = "Campamentos Educativos en el extranjero",
  sucursal = "Toluca",
}: HubspotFormProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)

  interface AdvisorConfig {
    phone: string
    ownerId: string
    name: string
  }

  // Catálogo unificado de Asesoras (WhatsApp + HubSpot Owner ID)
  const ADVISORS = {
    diana: { phone: "527223471356", ownerId: "1214456847", name: "Diana LET" },
    jessica: { phone: "527225531607", ownerId: "889181606", name: "Jessica Ramos" },
    fanny: { phone: "527224009106", ownerId: "428750394", name: "Fanny Reyes" },
    italia: { phone: "527228825193", ownerId: "420814713", name: "Italia Villaseñor" },
    viviana: { phone: "527223823905", ownerId: "1863829636", name: "Viviana Calzada" },
    vanessa: { phone: "527221111427", ownerId: "1727650296", name: "Vanessa Iniesta" },
    julieta: { phone: "527222061237", ownerId: "1840683466", name: "Julieta Fernandez" },
    sac: { phone: "527223985921", ownerId: "192321054", name: "Sac Nicté" },
    itzel: { phone: "527224215559", ownerId: "1252977135", name: "Itzel Romero" },
    mariaGdl: { phone: "523328220050", ownerId: "1540255673", name: "Maria Cantellano" },
    canada: { phone: "14164274406", ownerId: "1214456847", name: "Oficina Canadá" },
  }

  const selectAdvisor = (servicioRaw: string, sucursalRaw: string): AdvisorConfig => {
    const s = (servicioRaw || "").toLowerCase()
    const suc = (sucursalRaw || "").toLowerCase()

    if (suc.includes("guadalajara")) {
      return ADVISORS.mariaGdl
    }
    if (suc.includes("canad")) {
      return ADVISORS.canada
    }
    if (suc.includes("metepec")) {
      if (s.includes("turismo") || s.includes("viaje")) {
        const pool = [ADVISORS.fanny, ADVISORS.jessica]
        return pool[Math.floor(Math.random() * pool.length)]
      }
      if (s.includes("visa") || s.includes("migra")) {
        const pool = [ADVISORS.diana, ADVISORS.itzel]
        return pool[Math.floor(Math.random() * pool.length)]
      }
      return ADVISORS.diana
    }
    if (suc.includes("toluca")) {
      if (s.includes("campamento")) {
        const pool = [ADVISORS.vanessa, ADVISORS.julieta, ADVISORS.sac]
        return pool[Math.floor(Math.random() * pool.length)]
      }
      if (s.includes("turismo") || s.includes("viaje")) {
        return ADVISORS.italia
      }
      return ADVISORS.viviana
    }
    if (suc.includes("online") || suc.includes("linea") || suc.includes("línea")) {
      if (s.includes("turismo") || s.includes("viaje")) {
        const pool = [ADVISORS.fanny, ADVISORS.jessica, ADVISORS.italia]
        return pool[Math.floor(Math.random() * pool.length)]
      }
      if (s.includes("campamento")) {
        const pool = [ADVISORS.vanessa, ADVISORS.julieta, ADVISORS.sac]
        return pool[Math.floor(Math.random() * pool.length)]
      }
      if (s.includes("visa") || s.includes("migra")) {
        const pool = [ADVISORS.diana, ADVISORS.itzel, ADVISORS.viviana]
        return pool[Math.floor(Math.random() * pool.length)]
      }
      const pool = [ADVISORS.diana, ADVISORS.viviana]
      return pool[Math.floor(Math.random() * pool.length)]
    }

    if (s.includes("campamento")) {
      const pool = [ADVISORS.vanessa, ADVISORS.julieta, ADVISORS.sac]
      return pool[Math.floor(Math.random() * pool.length)]
    }

    return ADVISORS.diana
  }

  // Asesora seleccionada para esta sesión y render
  const selectedAdvisorRef = useRef<AdvisorConfig>(ADVISORS.diana)
  const selectedValuesRef = useRef({ servicio, sucursal })

  useEffect(() => {
    const assignedAdvisor = selectAdvisor(servicio, sucursal)
    selectedAdvisorRef.current = assignedAdvisor

    selectedValuesRef.current = {
      servicio,
      sucursal,
    }

    if (typeof window !== "undefined") {
      try {
        const url = new URL(window.location.href)
        url.searchParams.set("servicio_de_interes_web", servicio)
        url.searchParams.set("sucursal", sucursal)
        url.searchParams.set("hubspot_owner_id", assignedAdvisor.ownerId)
        window.history.replaceState({}, "", url.toString())
      } catch (e) {}
    }
  }, [servicio, sucursal])

  useEffect(() => {
    // 1. Cargar el script oficial de HubSpot V4 para este portal si no existe
    const scriptSrc = "https://js.hsforms.net/forms/embed/45258279.js"
    let script = document.querySelector<HTMLScriptElement>(`script[src="${scriptSrc}"]`)

    if (!script) {
      script = document.createElement("script")
      script.src = scriptSrc
      script.defer = true
      script.async = true
      document.body.appendChild(script)
    }

    // 2. Escuchar cuando el formulario termina de cargar
    const handleReady = () => {
      setIsLoaded(true)
    }

    let hasRedirected = false

    const redirectToWhatsApp = (finalServicio: string, finalSucursal: string) => {
      if (hasRedirected) return
      hasRedirected = true
      setIsRedirecting(true)

      trackLead({ content_name: "hubspot_form" })

      const advisor = selectedAdvisorRef.current || ADVISORS.diana
      const s = (finalServicio || "").toLowerCase()
      const nombreServicio = finalServicio || (s.includes("campamento") ? "Campamentos" : "programas")
      const mensaje =
        "Hola, solicito informes sobre " +
        nombreServicio +
        (finalSucursal ? " (Modalidad: " + finalSucursal + ")" : "") +
        "."

      console.log("[HubspotForm] Redirigiendo a:", advisor.name, "(ID:", advisor.ownerId, "WA:", advisor.phone, ")")
      const redirectUrl = "https://wa.me/" + advisor.phone + "?text=" + encodeURIComponent(mensaje)

      setTimeout(() => {
        window.location.href = redirectUrl
      }, 500)
    }

    const handleSubmissionSuccess = async (event: any) => {
      console.log("[HubspotForm] Envío exitoso detectado:", event)

      const finalServicio = selectedValuesRef.current.servicio
      const finalSucursal = selectedValuesRef.current.sucursal
      const assignedAdvisor = selectedAdvisorRef.current || ADVISORS.diana

      let submittedEmail = ""
      let submittedFirstname = ""
      let submittedLastname = ""
      let submittedPhone = ""

      // Método 1: Extraer a través de la API oficial HubSpotFormsV4
      try {
        const hsV4 = (window as any).HubSpotFormsV4
        const formInstance = hsV4 && hsV4.getFormFromEvent ? hsV4.getFormFromEvent(event) : null

        if (formInstance && typeof formInstance.getFormFieldValues === "function") {
          const values: Array<{ name: string; value: any }> = await formInstance.getFormFieldValues()
          console.log("[HubspotForm] Valores del formulario obtenidos (HubSpotFormsV4):", values)

          for (const item of values) {
            const key = (item.name || "").toLowerCase()
            if (key === "email" || key.endsWith("/email")) submittedEmail = item.value
            if (key === "firstname" || key.endsWith("/firstname")) submittedFirstname = item.value
            if (key === "lastname" || key.endsWith("/lastname")) submittedLastname = item.value
            if (key === "phone" || key.endsWith("/phone") || key.endsWith("/mobilephone")) submittedPhone = item.value
          }
        }
      } catch (err) {
        console.warn("[HubspotForm] Error con HubSpotFormsV4:", err)
      }

      // Método 2 (Fallback): Buscar en los inputs del DOM
      if (!submittedEmail) {
        try {
          const inputs = document.querySelectorAll<HTMLInputElement>(".hs-form-frame input, input")
          inputs.forEach((input) => {
            const name = (input.name || input.type || "").toLowerCase()
            if (name.includes("email") && input.value) submittedEmail = input.value
            if (name.includes("firstname") && input.value) submittedFirstname = input.value
            if (name.includes("lastname") && input.value) submittedLastname = input.value
            if ((name.includes("phone") || name.includes("tel")) && input.value) submittedPhone = input.value
          })
        } catch (e) {}
      }

      console.log("[HubspotForm] Datos listos para sincronizar:", {
        email: submittedEmail,
        ownerId: assignedAdvisor.ownerId,
        advisor: assignedAdvisor.name,
      })

      // Si tenemos el email del lead, sincronizamos con /api/leads ANTES de redirigir
      if (submittedEmail) {
        try {
          await fetch("/api/leads", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: submittedEmail,
              firstname: submittedFirstname,
              lastname: submittedLastname,
              phone: submittedPhone,
              sucursal: finalSucursal,
              servicio_de_interes_web: finalServicio,
              hubspot_owner_id: assignedAdvisor.ownerId,
            }),
          })
          console.log("[HubspotForm] Contacto asignado a", assignedAdvisor.name, "con éxito en CRM.")
        } catch (err) {
          console.error("[HubspotForm] Error llamando a /api/leads:", err)
        }
      }

      // Redirigir a WhatsApp de la asesora asignada
      redirectToWhatsApp(finalServicio, finalSucursal)
    }

    document.addEventListener("hs-form-event:on-ready", handleReady)
    document.addEventListener("hs-form-event:on-submission:success", handleSubmissionSuccess)

    // Fallback: Si en 3 segundos no dispara evento on-ready, ocultar spinner
    const timer = setTimeout(() => setIsLoaded(true), 3000)

    return () => {
      document.removeEventListener("hs-form-event:on-ready", handleReady)
      document.removeEventListener("hs-form-event:on-submission:success", handleSubmissionSuccess)
      clearTimeout(timer)
    }
  }, [])

  return (
    <div className="w-full min-h-[320px] relative">
      {/* Mensaje de éxito y redirección */}
      {isRedirecting && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 z-30 gap-3 text-center p-6 rounded-xl animate-fade-in">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <h3 className="text-xl font-bold text-gray-900">¡Registro completado!</h3>
          <p className="text-sm text-gray-600 max-w-sm">
            Te estamos canalizando de inmediato por WhatsApp con tu asesora asignada...
          </p>
        </div>
      )}


      {/* Spinner de carga del formulario HubSpot */}
      {!isLoaded && !isRedirecting && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500 font-medium">Cargando formulario oficial...</p>
        </div>
      )}

      {/* Contenedor del Formulario V4 de HubSpot */}
      <div
        className="hs-form-frame"
        data-region="na1"
        data-form-id="39c4c528-b672-4034-b0be-139a4eda92a4"
        data-portal-id="45258279"
      />
    </div>
  )
}
